import { ref, watch, onMounted, onUnmounted } from "vue";
import { useAuthStore } from "@/store/useAuthStore";
import { WORKSPACE_KEY, WORKSPACE_ID_KEY, WORKSPACE_NAME_KEY, WORKSPACE_LOCKED_KEY } from "@/constants/workspaceKeys";

const CHANNEL_NAME = "tripcart-workspace-sync";
const TAB_ID_KEY = "tripcart_tab_id";
const BROADCAST_COOLDOWN_KEY = "tripcart_broadcast_cooldown";
const LOG_PREFIX = "[WorkspaceSync]";
const ENABLE_DEBUG_LOGS = import.meta.env.DEV;
const BROADCAST_COOLDOWN_MS = 5000; // 5 seconds

interface WorkspaceChangeMessage {
    type: "WORKSPACE_CHANGED";
    agencyId: number;
    agencyName: string;
    tabId: string;
    timestamp: number;
}

/**
 * Enhanced logger
 */
const logger = {
    info: (...args: any[]) => {
        if (ENABLE_DEBUG_LOGS) {
            console.log(LOG_PREFIX, ...args);
        }
    },
    warn: (...args: any[]) => {
        if (ENABLE_DEBUG_LOGS) {
            console.warn(LOG_PREFIX, ...args);
        }
    },
    error: (...args: any[]) => console.error(LOG_PREFIX, ...args),
};

/**
 * Cross-tab workspace synchronization
 *
 * When workspace changes in another tab, shows blocking overlay:
 * - Message: "You've switched agencies. Please reload or click button..."
 * - Single button: "Switch to Active Agency" (reloads page)
 * - Blocks interaction with old agency to prevent data corruption
 */
export function useWorkspaceSync() {
    const showAgencyBlocker = ref(false); // Show blocking overlay
    const activeAgency = ref<{ id: number; name: string } | null>(null); // New active agency
    const currentAgency = ref<{ id: number; name: string } | null>(null); // Current (old) agency
    const hasBroadcastSupport = ref(true);

    const authStore = useAuthStore();

    let channel: BroadcastChannel | null = null;
    let consistencyInterval: number | null = null;
    let syncTimeout: number | null = null;
    let lastProcessedTimestamp: number = 0;
    let dismissedActiveAgencyId: number | null = null; // Agency ID the user chose to ignore
    let pendingNameWatch: (() => void) | null = null; // Watcher for backfilling agency name

    /**
     * Get or create unique tab identifier
     */
    const getTabId = (): string => {
        let tabId = sessionStorage.getItem(TAB_ID_KEY);
        if (!tabId) {
            tabId = `tab_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
            sessionStorage.setItem(TAB_ID_KEY, tabId);
        }
        return tabId;
    };

    /**
     * Persist workspace to localStorage
     */
    const persistWorkspace = (agencyId: number, agencyName: string): void => {
        const workspaceData = {
            id: agencyId,
            name: agencyName,
            timestamp: Date.now(),
        };
        localStorage.setItem(WORKSPACE_KEY, JSON.stringify(workspaceData));
    };

    /**
     * Get persisted workspace from localStorage
     */
    const getPersistedWorkspace = (): { id: number; name: string; timestamp: number } | null => {
        try {
            const data = localStorage.getItem(WORKSPACE_KEY);
            return data ? JSON.parse(data) : null;
        } catch {
            localStorage.removeItem(WORKSPACE_KEY);
            return null;
        }
    };

    /**
     * Check if we're in broadcast cooldown period
     */
    const isInBroadcastCooldown = (): boolean => {
        const cooldownUntil = parseInt(sessionStorage.getItem(BROADCAST_COOLDOWN_KEY) || "0");
        return Date.now() < cooldownUntil;
    };

    /**
     * Sync localStorage with backend state after fetchUser() completes.
     *
     * This prevents cross-browser stale data issues:
     * - Chrome switches A→B (updates Chrome's localStorage + backend)
     * - Edge's localStorage still has A (different browser)
     * - Edge reloads → fetchUser() returns B → this watch syncs localStorage to B
     * - Consistency check now finds no mismatch → no false popup
     *
     * Same-browser is unaffected because broadcastWorkspaceChange() sets a cooldown,
     * and this watch skips updates during cooldown.
     */
    watch(
        () => authStore.user?.agency?.id,
        (newAgencyId) => {
            if (newAgencyId && !isInBroadcastCooldown()) {
                // Skip localStorage sync when workspace is locked to prevent
                // this tab from overwriting the active workspace in localStorage
                if (sessionStorage.getItem(WORKSPACE_LOCKED_KEY)) {
                    logger.info("Skipping localStorage sync - workspace is locked");
                    return;
                }
                // Skip localStorage sync when the backend returned a different agency
                // than this tab's sessionStorage workspace. This happens in cross-browser
                // scenarios where another browser changed users.agency_id and this tab's
                // fetchUser() ran without X-Workspace-Id (e.g. fresh tab). Without this
                // guard, the watch would overwrite localStorage with the foreign agency,
                // causing the consistency check to show a false popup in other tabs.
                const tabWorkspaceId = sessionStorage.getItem(WORKSPACE_ID_KEY);
                if (tabWorkspaceId && parseInt(tabWorkspaceId) !== newAgencyId) {
                    logger.info(`Skipping localStorage sync - authStore agency (${newAgencyId}) differs from tab workspace (${tabWorkspaceId})`);
                    return;
                }
                const currentTitle = authStore.user?.agency?.title;
                if (currentTitle) {
                    const persisted = getPersistedWorkspace();
                    if (!persisted || persisted.id !== newAgencyId) {
                        persistWorkspace(newAgencyId, currentTitle);
                        logger.info(`Synced localStorage to backend state: ${currentTitle} (ID: ${newAgencyId})`);
                    }
                }
            }
        }
    );

    /**
     * Set broadcast cooldown
     */
    const setBroadcastCooldown = (): void => {
        const cooldownUntil = Date.now() + BROADCAST_COOLDOWN_MS;
        sessionStorage.setItem(BROADCAST_COOLDOWN_KEY, cooldownUntil.toString());
        logger.info(`Broadcast cooldown set for ${BROADCAST_COOLDOWN_MS / 1000}s`);
    };

    /**
     * Broadcast workspace change to all other tabs
     */
    const broadcastWorkspaceChange = (agencyId: number, agencyName: string): void => {
        const timestamp = Date.now();
        lastProcessedTimestamp = timestamp;

        // Clear any prior dismissal since this tab is actively switching
        dismissedActiveAgencyId = null;

        // Clear workspace lock — this tab is actively switching agencies
        sessionStorage.removeItem(WORKSPACE_LOCKED_KEY);

        // Set cooldown to prevent this tab from showing prompt to itself
        setBroadcastCooldown();

        // Persist to localStorage (cross-tab awareness)
        persistWorkspace(agencyId, agencyName);

        // Persist to sessionStorage (per-tab workspace for X-Workspace-Id header)
        sessionStorage.setItem(WORKSPACE_ID_KEY, String(agencyId));
        sessionStorage.setItem(WORKSPACE_NAME_KEY, agencyName);

        if (!hasBroadcastSupport.value) {
            logger.warn("BroadcastChannel not supported");
            return;
        }

        if (channel) {
            const message: WorkspaceChangeMessage = {
                type: "WORKSPACE_CHANGED",
                agencyId,
                agencyName,
                tabId: getTabId(),
                timestamp,
            };

            try {
                channel.postMessage(message);
                logger.info(`Broadcasted workspace change: ${agencyName} (ID: ${agencyId})`);
            } catch (error) {
                logger.error("Failed to broadcast:", error);
            }
        }
    };

    /**
     * User clicked "Switch to Active Agency" - reload to sync to new agency
     */
    const switchToActiveAgency = (): void => {
        logger.info(`Switching from "${currentAgency.value?.name}" to "${activeAgency.value?.name}"`);

        // Clear workspace lock — user chose to switch
        sessionStorage.removeItem(WORKSPACE_LOCKED_KEY);

        // Update sessionStorage to the active agency before reloading
        // so the X-Workspace-Id header sends the correct workspace on reload
        if (activeAgency.value) {
            sessionStorage.setItem(WORKSPACE_ID_KEY, String(activeAgency.value.id));
            sessionStorage.setItem(WORKSPACE_NAME_KEY, activeAgency.value.name);
        }

        logger.info("Reloading page to load active agency...");
        window.location.reload();
    };

    /**
     * User clicked "Keep Current" - dismiss blocker and continue with current workspace.
     * Safe because X-Workspace-Id header ensures all API requests go to the correct workspace.
     */
    const keepCurrentWorkspace = (): void => {
        logger.info(`Keeping current workspace: "${currentAgency.value?.name}"`);

        // Remember which active agency was dismissed so the consistency check
        // and broadcast handler won't re-show the blocker for the same agency.
        // This is per-tab only (no localStorage write) to avoid ping-pong
        // between tabs each overwriting localStorage and triggering the other.
        if (activeAgency.value) {
            dismissedActiveAgencyId = activeAgency.value.id;
        }

        // Lock the current agency in sessionStorage so that mapUserEssentials()
        // preserves this tab's agency data even when fetchUser() returns the
        // backend's (now different) active agency.
        const lockedAgency = authStore.user?.agency;
        if (lockedAgency) {
            sessionStorage.setItem(WORKSPACE_LOCKED_KEY, JSON.stringify(lockedAgency));
            logger.info(`Locked workspace to agency: ${lockedAgency.title} (ID: ${lockedAgency.id})`);
        }

        // sessionStorage still has the current workspace ID,
        // so all API requests will continue using the correct workspace
        showAgencyBlocker.value = false;
        activeAgency.value = null;
        currentAgency.value = null;
    };

    /**
     * Show blocking overlay when user is on old agency
     */
    const showAgencySwitchBlocker = (newAgencyId: number, newAgencyName: string): void => {
        // Capture current agency (old).
        // Fall back to sessionStorage when authStore isn't loaded yet
        // (consistency check fires on mount before fetchUser() resolves).
        const tabAgencyName = sessionStorage.getItem(WORKSPACE_NAME_KEY);
        const tabAgencyId = sessionStorage.getItem(WORKSPACE_ID_KEY);
        currentAgency.value = authStore.user?.agency
            ? { id: authStore.user.agency.id, name: authStore.user.agency.title }
            : (tabAgencyId && tabAgencyName)
                ? { id: parseInt(tabAgencyId), name: tabAgencyName }
                : null;

        // If name still unavailable (auth not resolved + name not in sessionStorage),
        // watch for fetchUser() to complete and backfill the name.
        // This covers a manual page refresh where WORKSPACE_NAME_KEY wasn't previously cached.
        if (!currentAgency.value) {
            // Stop any previous pending watch before creating a new one
            pendingNameWatch?.();
            pendingNameWatch = watch(
                () => authStore.user?.agency,
                (agency) => {
                    if (agency) {
                        if (showAgencyBlocker.value) {
                            currentAgency.value = { id: agency.id, name: agency.title };
                        }
                        pendingNameWatch?.();
                        pendingNameWatch = null;
                    }
                }
            );
        }

        // Set active agency (new)
        activeAgency.value = { id: newAgencyId, name: newAgencyName };

        // Show blocking overlay
        showAgencyBlocker.value = true;

        logger.warn("╔════════════════════════════════════════╗");
        logger.warn("║ AGENCY SWITCHED IN ANOTHER TAB        ║");
        logger.warn("╚════════════════════════════════════════╝");
        logger.warn(`  Old Agency: ${currentAgency.value?.name || 'unknown'}`);
        logger.warn(`  Active Agency: ${newAgencyName}`);
        logger.warn(`  Action: Blocking interactions to prevent data corruption`);
    };

    /**
     * Handle incoming workspace change messages
     */
    const handleMessage = (event: MessageEvent<WorkspaceChangeMessage>): void => {
        const { type, agencyId, agencyName, tabId, timestamp } = event.data;

        // Ignore messages from this tab
        if (tabId === getTabId()) {
            return;
        }

        if (type === "WORKSPACE_CHANGED") {
            // Ignore older messages
            if (timestamp <= lastProcessedTimestamp) {
                logger.info(`Ignoring older workspace change (${timestamp} <= ${lastProcessedTimestamp})`);
                return;
            }

            const currentAgencyId = authStore.user?.agency?.id;

            // Show blocker if we're on a different (old) agency (skip if already dismissed)
            if (currentAgencyId && currentAgencyId !== agencyId) {
                if (agencyId === dismissedActiveAgencyId) {
                    logger.info(`Ignoring broadcast for dismissed agency ${agencyId}`);
                    lastProcessedTimestamp = timestamp;
                    return;
                }
                logger.info(`Received agency switch from tab ${tabId.slice(0, 12)}...`);

                // Update timestamp
                lastProcessedTimestamp = timestamp;

                // Clear any pending timeout
                if (syncTimeout) {
                    window.clearTimeout(syncTimeout);
                }

                // Debounce by 100ms
                syncTimeout = window.setTimeout(() => {
                    showAgencySwitchBlocker(agencyId, agencyName);
                }, 100);
            } else if (currentAgencyId === agencyId) {
                // Already on target workspace, update timestamp
                logger.info(`Already on workspace ${agencyId}`);
                lastProcessedTimestamp = timestamp;

                // Clear dismissal state — the workspace that was dismissed is
                // back in sync, so a future switch to it must show the blocker
                // again. Must be outside the showAgencyBlocker guard below:
                // in the "Keep Current Workspace" scenario the blocker is
                // already false, so that block would never be entered.
                if (dismissedActiveAgencyId !== null) {
                    logger.info(`Cleared dismissal for agency ${dismissedActiveAgencyId} - workspace re-aligned`);
                    dismissedActiveAgencyId = null;
                }

                // Clear any pending debounced blocker
                if (syncTimeout) {
                    window.clearTimeout(syncTimeout);
                    syncTimeout = null;
                }

                // Auto-dismiss blocker if showing (e.g. user switched A→B→A
                // while this tab stayed on A and never synced)
                if (showAgencyBlocker.value) {
                    logger.info("Auto-dismissing blocker - current agency matches active agency");
                    showAgencyBlocker.value = false;
                    activeAgency.value = null;
                    currentAgency.value = null;
                }
            }
        }
    };

    /**
     * Check workspace consistency on mount
     */
    const checkWorkspaceConsistency = (): void => {
        // Skip if in cooldown (we just broadcasted)
        if (isInBroadcastCooldown()) {
            logger.info("Skipping consistency check - in broadcast cooldown");
            return;
        }

        // Skip if user is not authenticated (e.g. after logout or fresh login)
        let isAuthenticated = false;
        try {
            isAuthenticated = JSON.parse(localStorage.getItem("isAuthenticated") || "false");
        } catch {
            // Treat as unauthenticated if storage is corrupted
        }
        if (!isAuthenticated) {
            logger.info("Skipping consistency check - user not authenticated");
            return;
        }

        // Use sessionStorage workspace as the tab's source of truth.
        // This is more reliable than authStore because authStore may temporarily
        // reflect a different agency during cross-browser fetchUser() calls
        // (when X-Workspace-Id is absent and the backend falls back to users.agency_id).
        const tabWorkspaceId = sessionStorage.getItem(WORKSPACE_ID_KEY);

        // Skip if this tab hasn't initialized its workspace yet (fresh tab/login).
        // mapUserEssentials() will set the workspace ID shortly; running the check
        // now would compare stale localStorage against an uninitialized tab.
        if (!tabWorkspaceId) {
            logger.info("Skipping consistency check - tab workspace not yet initialized");
            return;
        }

        const currentAgencyId = parseInt(tabWorkspaceId);
        const persistedWorkspace = getPersistedWorkspace();

        if (persistedWorkspace) {
            // Update timestamp from localStorage
            if (persistedWorkspace.timestamp > lastProcessedTimestamp) {
                lastProcessedTimestamp = persistedWorkspace.timestamp;
            }

            // Show blocker if agency doesn't match (skip if user already dismissed this agency).
            // The dismiss-ID guard prevents re-showing the blocker for an agency the user
            // already chose to ignore via "Keep current agency". Without it, the periodic
            // consistency check would re-trigger the prompt every 30 seconds. When a
            // workspace lock is active the user has already committed to staying on this
            // agency, so the guard ensures the blocker stays suppressed for that agency.
            if (currentAgencyId && persistedWorkspace.id !== currentAgencyId && !showAgencyBlocker.value) {
                if (persistedWorkspace.id === dismissedActiveAgencyId) {
                    logger.info(`Skipping blocker - user already dismissed agency ${persistedWorkspace.id}`);
                    return;
                }
                logger.warn(`Tab out of sync (current: ${currentAgencyId}, active: ${persistedWorkspace.id})`);
                showAgencySwitchBlocker(persistedWorkspace.id, persistedWorkspace.name);
            }
        }
    };

    /**
     * Initialize BroadcastChannel
     */
    const initChannel = (): void => {
        if (typeof BroadcastChannel === "undefined") {
            logger.warn("BroadcastChannel not supported in this browser");
            hasBroadcastSupport.value = false;
            return;
        }

        try {
            channel = new BroadcastChannel(CHANNEL_NAME);
            channel.onmessage = handleMessage;
            logger.info("Channel initialized");

            // Check consistency on init
            checkWorkspaceConsistency();

            // Periodic check every 30 seconds
            consistencyInterval = window.setInterval(() => {
                if (!showAgencyBlocker.value && !isInBroadcastCooldown()) {
                    checkWorkspaceConsistency();
                }
            }, 30000);
        } catch (error) {
            logger.error("Failed to initialize BroadcastChannel:", error);
            hasBroadcastSupport.value = false;
        }
    };

    /**
     * Close BroadcastChannel
     */
    const closeChannel = (): void => {
        if (pendingNameWatch) {
            pendingNameWatch();
            pendingNameWatch = null;
        }

        if (syncTimeout) {
            window.clearTimeout(syncTimeout);
            syncTimeout = null;
        }

        if (consistencyInterval) {
            window.clearInterval(consistencyInterval);
            consistencyInterval = null;
        }

        if (channel) {
            channel.close();
            channel = null;
        }

        logger.info("Channel closed");
    };

    onMounted(initChannel);
    onUnmounted(closeChannel);

    return {
        // State
        showAgencyBlocker,
        activeAgency,
        currentAgency,
        hasBroadcastSupport,

        // Actions
        broadcastWorkspaceChange,
        switchToActiveAgency,
        keepCurrentWorkspace,
    };
}
